import { CircularProgress, MenuItem, InputAdornment, TextField, IconButton, Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import APIServices from "../../../services/app.service";
import CustomAutoComplete from "../../CustomComponents/CustomAutoComplete";
import { schemaLimit } from "../../../_helpers/Constant";
import { showToast } from "../../../Redux/reducers/toastSlice";
import { useDispatch } from "react-redux";

const dbConnectionParameters = {
  Databricks: ["catalogName", "httpPath", "token", "serverHostname", "authenticationType"],
  Snowflake: ["dataBase", "password", "server", "user", "authenticationType"],
  "Azure SQL": ["dataBase", "password", "server", "user", "authenticationType"],
  Oracle: ["password", "server", "sid", "user", "serviceType"],
  PostgreSQL: ["dataBase", "password", "server", "user", "authenticationType"],
  SQL: ["dataBase", "password", "server", "user", "authenticationType"],
};

const singleSchemaConnections = ["Databricks"];

export default function SelectSchema(props) {
  const {
    requiredFieldsChanges,
    setRequiredFieldsChanges,
    errors,
    control,
    connectionData,
    getValues,
    setValue,
    setError,
    schemaLoading,
    setSchemaLoading,
  } = props;
  const ref = useRef();
  const dispatch = useDispatch();
  const values = getValues();
  const connectionType = values?.connectionType;
  const [schemaList, setSchemaList] = useState([]);
  const [warehouseList, setWarehouseList] = useState([]);

  useEffect(() => {
    if ((values.schemas?.length || schemaList?.length) && requiredFieldsChanges) {
      setValue("schemas", []);
      setValue("warehouseId", "");
      setSchemaList([]);
      setWarehouseList([]);
    }
  }, [requiredFieldsChanges]);

  useEffect(() => {
    if (connectionData && Object.keys(connectionData).length !== 0) {
      handleSchemaFocus();
    }
  }, [connectionData]);

  const handleSchemaFocus = async () => {
    const newValues = getValues();
    const connectionType = newValues?.connectionType;
    const exstingAllSchemas = values.schemas;

    const connectionName = newValues.connectionName;

    const fields = dbConnectionParameters[connectionType] || [];

    const data = fields?.reduce(
      (acc, field) => {
        if (newValues && field in newValues) {
          acc[field] = newValues[field];
        }
        return acc;
      },
      { connectionType: connectionType, connectionName: connectionName },
    );

    const requiredFields = fields?.map((field) => newValues[field]);
    if (requiredFields.every(Boolean)) {
      try {
        setRequiredFieldsChanges(false);
        setSchemaLoading(true);
        const schemaResponse =
          connectionType === "Databricks"
            ? await APIServices.GetSchema_WarehouseList(data)
            : await APIServices.GetSchemaList(data);
        if (connectionType === "Databricks") {
          setWarehouseList(schemaResponse?.data?.warehouseRows);
        }

        const availableSchemas = schemaResponse?.data?.rows || schemaResponse?.data?.schemaRows || [];
        const isExistsSchemas = exstingAllSchemas?.every((schema) =>
          availableSchemas.some((each) => each?.SCHEMA_NAME === schema),
        );

        !isExistsSchemas && setValue("schemas", []);

        setSchemaList(schemaResponse?.data?.rows || schemaResponse?.data?.schemaRows);
      } catch (error) {
        fields.forEach((field) => {
          setError(field);
        });
        const errorMessage = error?.response?.data?.message || error.message;
        dispatch(showToast({ message: errorMessage, type: "error" }));
      } finally {
        setSchemaLoading(false);
      }
    } else {
      fields.forEach((field) => {
        if (!newValues[field]) {
          setError(field);
        }
      });
      dispatch(showToast({ message: "Fill in all required fields", type: "warning" }));
    }
  };

  const getWarehouseMenuList = () => {
    if (schemaLoading) {
      return (
        <MenuItem disabled>
          <CircularProgress color="inherit" size={20} />
          Loading...
        </MenuItem>
      );
    }

    return warehouseList?.map((option) => (
      <MenuItem key={option?.warehouse_id} value={option?.warehouse_id}>
        {option?.name}
      </MenuItem>
    ));
  };

  return (
    <>
      <Grid size={12}>
        <Box className="v-center" sx={{ float: "inline-end" }}>
          <Typography
            sx={{ fontSize: "10px" }}>{`Maximum we can select only ${schemaLimit} schemas per connection`}</Typography>
          <IconButton disabled size="small" sx={{ p: 0, pl: 0.4, pb: 0.18 }}>
            <InfoOutlinedIcon sx={{ fontSize: "14px" }} />
          </IconButton>
        </Box>
        <Controller
          control={control}
          name="schemas"
          rules={{ required: true }}
          render={({ field }) => (
            <CustomAutoComplete
              {...field}
              ref={ref}
              multiple={!singleSchemaConnections?.includes(connectionType)}
              label="Select Schema"
              size="small"
              fullWidth
              value={field.value || []}
              onChange={(e, value) => field.onChange(value)}
              options={schemaList?.map((each) => each?.SCHEMA_NAME)}
              errors={errors}
              // disabled={Boolean(schemaLoading)}
              onOpen={handleSchemaFocus}
              loading={schemaLoading}
              required={true}
              getOptionDisabled={() => field.value?.length >= schemaLimit}
            />
          )}
        />
      </Grid>

      {connectionType === "Databricks" && (
        <Grid size={12}>
          <Controller
            control={control}
            name="warehouseId"
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                sx={{ mt: 1 }}
                fullWidth
                select
                label={"Select Warehouse"}
                required
                size="small"
                value={field.value || ""}
                onChange={(e) => field.onChange(e.target.value)}
                error={Boolean(errors.warehouseId)} // Ensure this is a boolean
                disabled={Boolean(schemaLoading)}
                InputProps={{
                  endAdornment: schemaLoading && (
                    <InputAdornment position="end">
                      <CircularProgress color="inherit" size={20} />
                    </InputAdornment>
                  ),
                }}
                onFocus={handleSchemaFocus}>
                {getWarehouseMenuList()}
              </TextField>
            )}
          />
        </Grid>
      )}
    </>
  );
}
